import type { DeviceSummary, VenueSummary } from '@aiscoreboard/contracts';
import { ApiError } from './api.ts';
import type { MessageKey } from './i18n';
import { withRequestTimeout } from './requestTimeout.ts';

export type YouTubePrivacy = 'private' | 'unlisted' | 'public';
export type YouTubeStatus = 'disabled' | 'idle' | 'preparing' | 'waiting_for_stream'
  | 'live' | 'stopping' | 'ended' | 'error' | 'reauthorization_required';

export interface YouTubeDevice {
  deviceId: string;
  connectionId: string | null;
  streamId: string | null;
  privacy: YouTubePrivacy;
  status: YouTubeStatus;
  broadcastId: string | null;
  watchUrl: string | null;
  error: string | null;
}

export interface YouTubeConnection {
  id: string;
  channelId: string;
  title: string;
  status: 'connected' | 'reauthorization_required';
  createdAt: string;
}

export interface YouTubeVenue {
  configured: boolean;
  connections: YouTubeConnection[];
  devices: YouTubeDevice[];
}

export interface YouTubeStream {
  id: string;
  title: string;
  keyMatchesDeviceIds: string[];
}

export interface PairedYouTubeDevice {
  configured: boolean;
  device: DeviceSummary;
  connection: YouTubeConnection | null;
  broadcast: YouTubeDevice;
}

export function youtubeCallbackMessage(cookies: string, deviceId: string): MessageKey {
  const value = cookies.split(';').map(cookie => cookie.trim())
    .find(cookie => cookie.startsWith('youtube_error='))?.slice('youtube_error='.length);
  const prefix = `${encodeURIComponent(deviceId)}:`;
  if (!value?.startsWith(prefix)) return 'youtube.callbackError';
  const messages = {
    stream_key_mismatch: 'youtube.error.streamKey',
    channel_mismatch: 'youtube.error.channel',
    rtmp_mismatch: 'youtube.error.rtmp',
    stream_active: 'youtube.error.active',
    stream_reserved: 'youtube.error.reserved',
    oauth_expired: 'youtube.error.expired',
    configuration_changed: 'youtube.error.changed',
    auth: 'youtube.error.expired',
    permission: 'youtube.error.permission',
    quota: 'youtube.error.quota',
    network: 'youtube.error.network',
  } as const;
  const reason = value.slice(prefix.length);
  return Object.hasOwn(messages, reason) ? messages[reason as keyof typeof messages] : 'youtube.callbackError';
}

export async function youtubeRequest<T>(
  token: string, path: string, method = 'GET', body?: unknown, signal?: AbortSignal,
): Promise<T> {
  return withRequestTimeout(20_000, async requestSignal => {
    const response = await fetch(path, {
      method,
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: requestSignal,
      cache: 'no-store',
    });
    if (!response.ok) {
      let message = `YouTube request failed (${response.status})`;
      if (response.headers.get('content-type')?.includes('application/json')) {
        const result: { error?: unknown } = await response.json();
        if (typeof result.error === 'string') message = result.error;
      }
      throw new ApiError(response.status, message);
    }
    return response.status === 204 ? undefined as T : response.json() as Promise<T>;
  }, signal);
}

export const youtubeVenuePath = (id: string): string => `/api/youtube/venues/${encodeURIComponent(id)}`;
export const youtubeDevicePath = (id: string): string => `/api/youtube/devices/${encodeURIComponent(id)}`;

export function getPairedYouTubeDevice(token: string, id: string, signal?: AbortSignal): Promise<PairedYouTubeDevice> {
  return youtubeRequest(token, `${youtubeDevicePath(id)}/paired`, 'GET', undefined, signal);
}

export function googleAuthorizationUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'accounts.google.com'
      && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
}

export async function authorizeYouTubeCamera(token: string, id: string): Promise<string | null> {
  const result = await youtubeRequest<{ url: string }>(token, `${youtubeDevicePath(id)}/authorize`, 'POST', {});
  return googleAuthorizationUrl(result.url);
}

export function listYouTubeVenues(token: string): Promise<{ venues: VenueSummary[] }> {
  return youtubeRequest(token, '/api/venues');
}

export function getYouTubeVenue(token: string, id: string, signal?: AbortSignal): Promise<YouTubeVenue> {
  return youtubeRequest(token, youtubeVenuePath(id), 'GET', undefined, signal);
}

export function safeYouTubeWatchUrl(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'www.youtube.com'
      && url.pathname === '/watch' ? url.href : null;
  } catch {
    return null;
  }
}
